import { Loader } from "@googlemaps/js-api-loader";
import axios from 'axios';
import { gsap } from "gsap";

let markers = [];
let infoWindow;
let map;
let markerClass;
let mapContainer;
let activeMarker;
const parser = new DOMParser();
// A marker with a custom inline SVG.
const pinSvgString = `<svg xmlns="http://www.w3.org/2000/svg" width="33" height="42" viewBox="0 0 33 42"><path fill="#25434f" stroke="#fff" stroke-width="2" d="M16.737 1.737c8.25 0 15 6.5 15 14.5 0 3.167-1.083 6.167-3 8.667L17.404 39.737l-.167.167c-.333.25-.917.166-1.167-.167L4.737 24.904c-1.917-2.5-3-5.5-3-8.667 0-8 6.75-14.5 15-14.5Zm0 8a7 7 0 1 0 0 14 7 7 0 0 0 0-14Z"/></svg>`;

function clearActiveMarker(marker) {
  // Remove class from any previously active marker
  if (activeMarker && activeMarker !== marker) {
    activeMarker.classList.remove('marker-is-active');
    scaleDownMarker(activeMarker);
    activeMarker = null;
  }
}

function scaleUpMarker(marker) {
  gsap.to(marker.content, { 
    ease: "back.out(1.7)",
    transformOrigin: "50% 100%",
    scale: 1.2, 
    duration: 0.2 
  });
}

function scaleDownMarker(marker) {
  gsap.to(marker.content, { scale: 1, duration: 0.05, ease: 'none' });
}

function updateMarkers(urlParam) {
  // As we also run this function in the filters.js, check the map is actually on the page - it will be for the projects page but not the resources page..
  if (!mapContainer) return;
  // Remove existing markers from map
  if (markers.length) {
    markers.forEach((marker) => marker.setMap(null));
    markers = [];
  }

  // Make sure we request either projects.json or pilots.json
  const url = makeMapRequestUrl(urlParam);

  // Get a matching list of projects
  axios.get(url).then((res) => {
    const items = res.data.data;

    items.forEach((item) => {
      if (!item.latLng) return;
      const lat = item.latLng.split(',')[0];
      const lng = item.latLng.split(',')[1];
      const heading = item.title;
      const url = item.url;

      if (!lat || !lng) return;

      const pinSvg = parser.parseFromString(pinSvgString, 'image/svg+xml').documentElement;

      const marker = new markerClass({
        map,
        position: { lat: parseFloat(lat), lng: parseFloat(lng) },
        title: heading,
        gmpClickable: true,
        content: pinSvg,
      });

      // Add a click listener for each marker, and set up the info window.
      marker.addListener("click", () => {
        clearActiveMarker(marker);
        // Mark this one active and show the window
        activeMarker = marker;
        marker.classList.add('marker-is-active');
        infoWindow.close();
        setInfoWindowContent({text: heading, link: url});
        infoWindow.open(marker.map, marker);
      });

      marker.content.addEventListener('mouseenter', () => {
        scaleUpMarker(marker);
      });

      marker.content.addEventListener('mouseleave', () => {
        if (marker.classList.contains('marker-is-active')) return;
        scaleDownMarker(marker);
      });

      markers.push(marker);
    });
  });
}

function setInfoWindowContent(param) {
  if (!infoWindow) return;
  const { text, link } = param;
  infoWindow.setContent(
    `<div class="map-info-window">
    ${text ? '<h3 class="map-info-window__heading">' + text + '</h3>' : ''}
    ${link ? '<a class="map-info-window__link" href="' + link + '">Find out more</a>' : ''}
    </div>`
  );
}

function makeMapRequestUrl(urlParam) {
  const mapChannel = mapContainer.dataset.channel || 'projects';
  const mapUrlObject = new URL(urlParam || location.href);
  // The map uses the Element API plugin to get the entries from Craft.
  // Note: the map view ignores pagination. If we're getting the channel from the the location.href on page load, we use the "limit" parameter of the .split method to make sure we get "/projects" or "/pilots" etc, and not "page2" for example.
  let pathname = mapUrlObject.pathname.split('/', 2).pop();
  pathname = pathname || mapChannel;
  // Check pathname includes json, add it if not. Remember, we don't care about the "page" portion of the path if it's present as we don't need it for the Element API request:
  if (!pathname.includes('.json')) {
    mapUrlObject.pathname = `${pathname}.json`
  }
  return mapUrlObject.toString();
}

function loadGeoJson(mapAreaUrls) {
  if (!mapAreaUrls) return;
  mapAreaUrls.forEach((url) => {
    map.data.loadGeoJson(url);
  });
  // Set shape styles
  map.data.setStyle({ 
    fillColor: "#FF880A",
    fillOpacity: 0.5, 
    strokeWeight: 1, 
    strokeColor: '#FF880A' 
  });
  
  // Change opacity on 'hover'
  map.data.addListener('mouseover', function(event) {
    map.data.overrideStyle(event.feature, { fillOpacity: 0.8 });
  });
  
  // Revert opacity
  map.data.addListener('mouseout', function() {
    // infoWindow.close(map);
    map.data.revertStyle();
  });

  // Setup hotlink redirects for when area is clicked
  map.data.addListener('click', function(event) {
    const hotlink = event.feature.getProperty('hotlink');
    if (hotlink) {
      document.location.href = hotlink;
    }
  });
}

function init() {
  mapContainer = document.getElementById("projects-map-embed");
  if (!mapContainer) return;

  const mapAreaUrls = mapContainer.dataset.mapData ? mapContainer.dataset.mapData.split('|') : null;

  const loader = new Loader({
    apiKey: "AIzaSyBOu0xJB2Dmae9R5bW2DfSyIoIIV34Iopg"
  });
  
  loader.load().then(async () => {
    const { Map, InfoWindow } = await google.maps.importLibrary("maps");
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker");
    markerClass = AdvancedMarkerElement;
  
    map = new Map(mapContainer, {
      center: { lat: 51.509865, lng: -0.118092 },
      mapId: '7bc055b4a4fc272e',
      zoom: 8,
    });

    // Create an info window to share between markers.
    infoWindow = new InfoWindow({
      maxWidth: 250,
      pixelOffset: new google.maps.Size(0, -5)
    });

    // Detect when InfoWindow is closed by the user
    infoWindow.addListener("closeclick", clearActiveMarker);
  }).then(() => {
    updateMarkers();
    loadGeoJson(mapAreaUrls);
  }).catch(error => {
    // Handle any errors
    console.error('Error occurred:', error);
  });
}

const projectsMap = {
  init,
  updateMarkers
}

export default projectsMap;